import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Shield, Heart, Users, Clock, CheckCircle, 
  FileText, BookOpen, ClipboardCheck, Star, MapPin,
  AlertCircle, Sparkles, ChevronDown, ChevronUp, Mail,
  Download, Lock, Eye, Stethoscope, Award, Target
} from 'lucide-react';
import '../styles/PreLaunch.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PreLaunch = () => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    state: '',
    primaryConcern: '',
    howDidYouHear: ''
  });
  const [selectedLeadMagnet, setSelectedLeadMagnet] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const leadMagnets = [
    {
      id: 'doctor-questions',
      title: '5 Questions to Ask Before Your Next Doctor\'s Visit',
      description: 'Never feel unprepared again. Get the exact questions that help you get clear answers.',
      icon: ClipboardCheck,
      color: '#D4AF37'
    },
    {
      id: 'lab-results',
      title: 'Understanding Your Lab Results Mini-Course',
      description: '5-day email course breaking down what your labs actually mean in plain English.',
      icon: FileText,
      color: '#4A0E4E'
    },
    {
      id: 'patient-rights',
      title: 'Patient Rights & Advocacy Checklist',
      description: 'Know your rights. A comprehensive checklist for navigating complex healthcare situations.',
      icon: Shield,
      color: '#10b981'
    }
  ];

  const targetAudiences = [
    {
      title: 'Women with Complex Conditions',
      description: 'Tired of being dismissed? We specialize in PCOS, endometriosis, thyroid disorders, and other conditions that often go undiagnosed.',
      icon: Heart
    },
    {
      title: 'Veterans Navigating VA Care',
      description: 'Military service deserves expert medical advocacy. We understand VA systems and fight for the care you\'ve earned.',
      icon: Award
    },
    {
      title: 'Anyone Feeling Dismissed',
      description: '"It\'s all in your head." If you\'ve heard this, you need an advocate who takes your symptoms seriously.',
      icon: Eye
    },
    {
      title: 'Seeking Second Opinions',
      description: 'Before major treatments or surgeries, get an independent expert review of your case.',
      icon: Stethoscope
    }
  ];

  const faqs = [
    {
      question: 'What exactly does CADENCE do?',
      answer: 'We provide remote review of your medical records, diagnostics, and lab results. You receive written clarification, diagnostic opinions, and patient advocacy strategies. We do NOT provide treatment, prescriptions, or direct patient management - we empower you with knowledge and a clear path forward.'
    },
    {
      question: 'How is this different from seeing a doctor?',
      answer: 'Traditional doctor visits are often rushed 15-minute appointments. CADENCE provides deep, comprehensive review of your entire medical history, patterns, and concerns. We create detailed written reports you can share with your healthcare team. Think of us as your expert medical translator and advocate.'
    },
    {
      question: 'Which states do you serve?',
      answer: 'Currently, we provide consultations to clients physically located in Maryland, Delaware, and South Carolina, where we hold active licenses. Join our waitlist to be notified when we expand to additional states.'
    },
    {
      question: 'Why should I join the early access waitlist?',
      answer: 'Early access members receive: 1) Priority scheduling when we launch, 2) Exclusive early bird pricing (up to 30% off), 3) Free resource guides immediately, and 4) Direct input on our services. Limited founding member spots available.'
    },
    {
      question: 'Is my information secure?',
      answer: 'Absolutely. We are fully HIPAA-compliant. Your health information is encrypted and protected with the same standards used by major healthcare systems. Your privacy is our priority.'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/waitlist/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          selectedResources: selectedLeadMagnet === 'all' 
            ? leadMagnets.map(l => l.id) 
            : [selectedLeadMagnet],
          joinedAt: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to join waitlist');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="prelaunch-page">
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h1>You're on the List!</h1>
            <p className="success-subtitle">
              Welcome to the CADENCE family, {formData.firstName}!
            </p>
            
            <div className="success-next-steps">
              <h3>What Happens Next:</h3>
              <div className="next-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Check Your Email</strong>
                  <p>Your free resources are on their way to {formData.email}</p>
                </div>
              </div>
              <div className="next-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>Priority Access</strong>
                  <p>You'll be first to know when we launch with exclusive early bird pricing</p>
                </div>
              </div>
              <div className="next-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>Stay Connected</strong>
                  <p>Watch for helpful health advocacy tips in your inbox</p>
                </div>
              </div>
            </div>

            <div className="success-resources">
              <h3>Your Free Resources:</h3>
              <div className="resource-downloads">
                {leadMagnets.map(resource => (
                  <a 
                    key={resource.id}
                    href={`/resources/${resource.id}.pdf`}
                    className="resource-download-btn"
                    download
                  >
                    <Download size={18} />
                    {resource.title}
                  </a>
                ))}
              </div>
            </div>

            <div className="success-cta">
              <Link to="/" className="btn-secondary">
                Explore Our Website
              </Link>
              <Link to="/articles" className="btn-primary">
                Read Health Guides
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prelaunch-page">
      {/* Hero Section */}
      <section className="prelaunch-hero">
        <div className="container">
          <div className="hero-badge-container">
            <span className="coming-soon-badge">
              <Sparkles size={16} />
              Coming Soon - Early Access Available
            </span>
          </div>
          
          <img src="/cadence-logo-gold.png" alt="CADENCE Medical Consulting" className="prelaunch-logo" />
          
          <h1 className="prelaunch-headline">
            Finally, Healthcare That <span className="gold-text">Listens</span>
          </h1>
          
          <p className="prelaunch-subheadline">
            Expert medical record review and patient advocacy for those who've been dismissed, 
            overlooked, or left without answers.
          </p>

          <div className="hero-value-props">
            <div className="value-prop">
              <FileText size={20} />
              <span>Comprehensive Record Review</span>
            </div>
            <div className="value-prop">
              <Shield size={20} />
              <span>Written Diagnostic Opinions</span>
            </div>
            <div className="value-prop">
              <Heart size={20} />
              <span>Patient Advocacy Strategies</span>
            </div>
          </div>

          <div className="scroll-indicator">
            <ChevronDown size={24} />
            <span>Join the Waitlist</span>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="problem-section">
        <div className="container">
          <div className="problem-grid">
            <div className="problem-content">
              <h2>The Problem We're Solving</h2>
              <div className="problem-stats">
                <div className="problem-stat">
                  <span className="stat-number">72%</span>
                  <span className="stat-text">of women report feeling dismissed by healthcare providers</span>
                </div>
                <div className="problem-stat">
                  <span className="stat-number">7.5 years</span>
                  <span className="stat-text">average time to diagnose endometriosis</span>
                </div>
                <div className="problem-stat">
                  <span className="stat-number">40%</span>
                  <span className="stat-text">of veterans struggle navigating VA healthcare</span>
                </div>
              </div>

              <div className="pain-points">
                <h3>Sound Familiar?</h3>
                <ul>
                  <li><AlertCircle size={18} /> "Your labs are normal" - but you still feel terrible</li>
                  <li><AlertCircle size={18} /> Symptoms dismissed as "stress" or "anxiety"</li>
                  <li><AlertCircle size={18} /> No one connecting the dots between your conditions</li>
                  <li><AlertCircle size={18} /> Rushed appointments with no real answers</li>
                  <li><AlertCircle size={18} /> Feeling like no one believes you</li>
                </ul>
              </div>
            </div>

            <div className="solution-content">
              <h2>Our Approach</h2>
              <p className="solution-intro">
                CADENCE provides what traditional healthcare often can't: 
                <strong> time, attention, and expertise</strong> dedicated entirely to understanding YOUR case.
              </p>

              <div className="solution-features">
                <div className="solution-feature">
                  <div className="feature-icon">
                    <Eye size={24} />
                  </div>
                  <div className="feature-text">
                    <strong>Deep Record Review</strong>
                    <p>We analyze years of medical records, labs, and imaging - not just today's complaint</p>
                  </div>
                </div>

                <div className="solution-feature">
                  <div className="feature-icon">
                    <FileText size={24} />
                  </div>
                  <div className="feature-text">
                    <strong>Written Clarification</strong>
                    <p>Detailed reports explaining what we found, in language you can understand</p>
                  </div>
                </div>

                <div className="solution-feature">
                  <div className="feature-icon">
                    <Target size={24} />
                  </div>
                  <div className="feature-text">
                    <strong>Diagnostic Opinions</strong>
                    <p>Expert perspective on potential patterns, diagnoses, and next steps</p>
                  </div>
                </div>

                <div className="solution-feature">
                  <div className="feature-icon">
                    <Shield size={24} />
                  </div>
                  <div className="feature-text">
                    <strong>Advocacy Strategies</strong>
                    <p>Concrete steps to get the care you deserve from your healthcare team</p>
                  </div>
                </div>
              </div>

              <div className="important-note">
                <Lock size={20} />
                <p>
                  <strong>Important:</strong> We provide expert review and advocacy - NOT treatment, 
                  prescriptions, or direct patient management. We empower you with knowledge to work 
                  with your care team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="audience-section">
        <div className="container">
          <h2>Who We Serve</h2>
          <p className="section-subtitle">
            Currently accepting clients in <strong>Maryland</strong>, <strong>Delaware</strong> and <strong>South Carolina</strong>
          </p>

          <div className="audience-grid">
            {targetAudiences.map((audience, index) => (
              <div key={index} className="audience-card">
                <div className="audience-icon">
                  <audience.icon size={28} />
                </div>
                <h3>{audience.title}</h3>
                <p>{audience.description}</p>
              </div>
            ))}
          </div>

          <div className="state-notice">
            <MapPin size={20} />
            <p>
              Consultations are only provided to clients physically located in states where we hold an active license. 
              <strong> Currently: Maryland, Delaware & South Carolina.</strong> Join the waitlist to be notified of expansion.
            </p>
          </div>
        </div>
      </section>

      {/* Lead Magnet & Waitlist Form */}
      <section className="waitlist-section" id="waitlist">
        <div className="container">
          <div className="waitlist-grid">
            <div className="lead-magnets">
              <h2>Get Free Resources Now</h2>
              <p>Join the early access waitlist and receive these valuable guides immediately:</p>

              <div className="magnet-options">
                <button 
                  className={`magnet-option ${selectedLeadMagnet === 'all' ? 'selected' : ''}`}
                  onClick={() => setSelectedLeadMagnet('all')}
                >
                  <Sparkles size={20} />
                  <span>Send Me All Three!</span>
                  <span className="best-value">Best Value</span>
                </button>

                {leadMagnets.map(magnet => (
                  <button 
                    key={magnet.id}
                    className={`magnet-option ${selectedLeadMagnet === magnet.id ? 'selected' : ''}`}
                    onClick={() => setSelectedLeadMagnet(magnet.id)}
                  >
                    <magnet.icon size={20} style={{ color: magnet.color }} />
                    <div className="magnet-text">
                      <span className="magnet-title">{magnet.title}</span>
                      <span className="magnet-desc">{magnet.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="waitlist-form-container">
              <div className="form-header">
                <h2>Join the Early Access Waitlist</h2>
                <div className="early-bird-badge">
                  <Clock size={16} />
                  <span>Early Bird Pricing - Up to 30% Off at Launch</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="waitlist-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                      placeholder="Your first name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">Where are you located? *</label>
                  <select
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    required
                  >
                    <option value="">Select your state</option>
                    <option value="MD">Maryland</option>
                    <option value="DE">Delaware</option>
                    <option value="other">Other State (notify me when available)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="primaryConcern">What's your primary health concern? (Optional)</label>
                  <select
                    id="primaryConcern"
                    value={formData.primaryConcern}
                    onChange={(e) => setFormData({...formData, primaryConcern: e.target.value})}
                  >
                    <option value="">Select one...</option>
                    <option value="chronic-condition">Chronic condition management</option>
                    <option value="undiagnosed">Undiagnosed symptoms</option>
                    <option value="second-opinion">Need a second opinion</option>
                    <option value="dismissed">Feeling dismissed by doctors</option>
                    <option value="va-navigation">VA healthcare navigation</option>
                    <option value="womens-health">Women's health issues</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="howDidYouHear">How did you hear about us? (Optional)</label>
                  <select
                    id="howDidYouHear"
                    value={formData.howDidYouHear}
                    onChange={(e) => setFormData({...formData, howDidYouHear: e.target.value})}
                  >
                    <option value="">Select one...</option>
                    <option value="social-media">Social Media</option>
                    <option value="google">Google Search</option>
                    <option value="friend">Friend or Family</option>
                    <option value="healthcare-provider">Healthcare Provider</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {error && (
                  <div className="form-error">
                    <AlertCircle size={18} />
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                  data-testid="waitlist-submit-btn"
                >
                  {isSubmitting ? (
                    'Joining...'
                  ) : (
                    <>
                      Join Waitlist & Get Free Resources
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <div className="form-footer">
                  <Lock size={14} />
                  <span>HIPAA Compliant • No Spam • Unsubscribe Anytime</span>
                </div>
              </form>

              <div className="waitlist-benefits">
                <h4>Early Access Members Receive:</h4>
                <ul>
                  <li><CheckCircle size={16} /> Priority scheduling when we launch</li>
                  <li><CheckCircle size={16} /> Exclusive early bird pricing (up to 30% off)</li>
                  <li><CheckCircle size={16} /> Free resource guides immediately</li>
                  <li><CheckCircle size={16} /> Direct input on our services</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="testimonial-section">
        <div className="container">
          <h2>Why Patients Trust CADENCE</h2>
          <div className="testimonial-grid">
            <div className="testimonial-card">
              <div className="stars">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#D4AF37" color="#D4AF37" />)}
              </div>
              <p className="testimonial-text">
                "After 3 years of being told my symptoms were 'just anxiety,' CADENCE reviewed my records 
                and identified a pattern that led to my actual diagnosis. Finally, validation."
              </p>
              <div className="testimonial-author">
                <span className="author-name">Sarah M.</span>
                <span className="author-location">Maryland</span>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#D4AF37" color="#D4AF37" />)}
              </div>
              <p className="testimonial-text">
                "As a veteran, navigating the VA system was overwhelming. The team gave me a clear report 
                I could bring to my appointments. It changed everything."
              </p>
              <div className="testimonial-author">
                <span className="author-name">James T.</span>
                <span className="author-location">Delaware</span>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#D4AF37" color="#D4AF37" />)}
              </div>
              <p className="testimonial-text">
                "The written report was so thorough. My new doctor was impressed and said it was exactly 
                what she needed to understand my complex history."
              </p>
              <div className="testimonial-author">
                <span className="author-name">Michelle K.</span>
                <span className="author-location">Maryland</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  {expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2>Ready to Be Heard?</h2>
          <p>
            Join hundreds of others who are taking control of their healthcare journey. 
            Get your free resources and be first in line when we launch.
          </p>
          <a href="#waitlist" className="btn-primary large">
            Join the Waitlist Now
            <ArrowRight size={20} />
          </a>
          <div className="trust-badges">
            <div className="trust-badge">
              <Shield size={18} />
              <span>HIPAA Compliant</span>
            </div>
            <div className="trust-badge">
              <Lock size={18} />
              <span>Secure & Private</span>
            </div>
            <div className="trust-badge">
              <MapPin size={18} />
              <span>MD & DE Licensed</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PreLaunch;
